// Consent persistence and Google Consent Mode v2 state. This module owns the
// persisted decision and the current document's effective consent; it never
// imports the GTM loader, keeping the dependency direction one-way.

import {
  CONSENT_RECORD_VERSION,
  CONSENT_STORAGE_KEY,
  consentSignalsFor,
  defaultConsentSignals,
  hasAuthCallbackState,
  parseStoredConsent,
  resolveConsentDecision,
  type ConsentDecision,
  type ConsentSignals,
  type DefaultConsentSignals
} from './analyticsCore';
import { clearBookingReceipts } from './bookingReceiptStorage';
import { bootstrapMeasurement, captureAfterAllow, getServerGpc, getServerDenied, revokeMeasurement } from './measurement';
import { MEASUREMENT_POLICY_VERSION } from './measurementContract';
import { setMeasurementDeniedInDocument } from './measurementPreference';
import { isAnalyticsEligible } from './publicRoutes';

const EXPLICIT_ANALYTICS_RECEIPT = 'lw_explicit_analytics_v1';

function hasExplicitAnalyticsChoice(): boolean {
  try { return window.localStorage.getItem(EXPLICIT_ANALYTICS_RECEIPT) === MEASUREMENT_POLICY_VERSION; }
  catch { return false; }
}

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

export type ConsentEvent =
  | { type: 'decision'; decision: ConsentDecision }
  | { type: 'choices_requested' };

type Listener = (event: ConsentEvent) => void;

const listeners = new Set<Listener>();
let effectiveConsent: ConsentDecision | null = null;
let consentModeReady = false;
let defaultsAttempted = false;
let withdrawnInDocument = false;

function pushConsentCommand(
  command: 'default' | 'update',
  signals: ConsentSignals | DefaultConsentSignals
): boolean {
  try {
    window.dataLayer = window.dataLayer || [];

    // gtag.js requires the Arguments object shape for consent commands.
    // eslint-disable-next-line prefer-rest-params
    const gtag = function (..._args: unknown[]) { window.dataLayer?.push(arguments); };
    gtag('consent', command, signals);
    return true;
  } catch {
    return false;
  }
}

export function getStoredConsent(): ConsentDecision | null {
  try {
    return parseStoredConsent(window.localStorage.getItem(CONSENT_STORAGE_KEY))?.decision ?? null;
  } catch {
    return null;
  }
}

/** True only when the decision was written and reads back intact. */
function persist(decision: ConsentDecision): boolean {
  try {
    window.localStorage.setItem(
      CONSENT_STORAGE_KEY,
      JSON.stringify({
        decision,
        version: CONSENT_RECORD_VERSION,
        at: new Date().toISOString()
      })
    );
    return getStoredConsent() === decision;
  } catch {
    // Storage can be unavailable; effective consent still applies to this page.
    return false;
  }
}

/**
 * Global Privacy Control (navigator.globalPrivacyControl) is a standing
 * opt-out. It overrides any saved choice, and no banner path can grant past it.
 */
export function hasGlobalPrivacyControl(): boolean {
  try {
    return getServerGpc() === true || (navigator as Navigator & { globalPrivacyControl?: unknown }).globalPrivacyControl === true;
  } catch {
    return false;
  }
}

/**
 * Install default-denied before React or tags, then resolve the visitor's
 * region and current notice before GTM can arm. Saved denials and Global
 * Privacy Control always win. Internal/auth/unknown entry documents receive
 * no dataLayer at all because they can never load analytics.
 */
export function installConsentDefaults(): void {
  if (defaultsAttempted) return;
  defaultsAttempted = true;
  const decision = getStoredConsent() === 'denied' || hasGlobalPrivacyControl() ? 'denied' : null;
  withdrawnInDocument = decision === 'denied';
  setMeasurementDeniedInDocument(withdrawnInDocument);
  if (decision === 'denied') clearBookingReceipts();

  // The first-party bootstrap also retries a pending denial on private pages;
  // those pages still never get a dataLayer or a Google tag.
  void bootstrapMeasurement().then(() => {
    if (decision === 'denied') void revokeMeasurement();
  });

  window.addEventListener('storage', applyOptOutFromAnotherTab);
  if (!isAnalyticsEligible(window.location.pathname)) return;
  if (hasAuthCallbackState(window.location.search, window.location.hash)) return;

  if (!pushConsentCommand('default', defaultConsentSignals())) return;
  consentModeReady = true;
  effectiveConsent = 'denied';

}

/** Called after the notice is rendered, or a current notice receipt is found. */
export async function resolveAnalyticsAfterNotice(): Promise<boolean> {
  const context = await bootstrapMeasurement();
  if (!context || context.gpc || context.denied !== false || withdrawnInDocument || resolveConsentDecision(getStoredConsent(), hasGlobalPrivacyControl(), context?.analyticsDefault === true, hasExplicitAnalyticsChoice()) === 'denied') return false;
  if (effectiveConsent === 'granted') return true;
  if (consentModeReady && pushConsentCommand('update', consentSignalsFor('granted'))) {
    effectiveConsent = 'granted';
    return true;
  }
  return false;
}

/** Another tab saved an opt-out: deny Consent Mode here and tell subscribers. */
function applyOptOutFromAnotherTab(event: StorageEvent): void {
  if (event.key !== CONSENT_STORAGE_KEY) return;
  if (getStoredConsent() !== 'denied') return;
  withdrawnInDocument = true;
  setMeasurementDeniedInDocument(true);
  if (consentModeReady) pushConsentCommand('update', consentSignalsFor('denied'));
  effectiveConsent = 'denied';
  void revokeMeasurement();
  listeners.forEach((listener) => listener({ type: 'decision', decision: 'denied' }));
}

export function getEffectiveConsent(): ConsentDecision | null {
  return effectiveConsent;
}

export function hasEffectiveAnalyticsConsent(): boolean {
  // Re-read the saved choice on every check so an opt-out saved in another tab
  // stops this tab's emissions at once; GPC wins here as everywhere else.
  return consentModeReady && effectiveConsent === 'granted'
    && getStoredConsent() !== 'denied' && getServerDenied() === false && !hasGlobalPrivacyControl();
}

/** With requireSaved, an unsaved grant leaves the current denial in place. */
export function grantAnalyticsConsent(requireSaved = false): boolean {
  if (hasGlobalPrivacyControl() || getServerGpc() !== false || getServerDenied() !== false) return false;
  const saved = persist('granted');
  if (requireSaved && !saved) return false;
  try { window.localStorage.setItem(EXPLICIT_ANALYTICS_RECEIPT, MEASUREMENT_POLICY_VERSION); }
  catch { if (requireSaved) return false; }
  withdrawnInDocument = false;
  setMeasurementDeniedInDocument(false);
  const updated = consentModeReady && pushConsentCommand('update', consentSignalsFor('granted'));
  effectiveConsent = updated ? 'granted' : 'denied';
  void captureAfterAllow();
  listeners.forEach((listener) => listener({ type: 'decision', decision: 'granted' }));
  return updated || (!consentModeReady && saved);
}

/** Returns whether the opt-out was saved, so callers know a reload keeps it. */
export function denyAnalyticsConsent(): boolean {
  withdrawnInDocument = true;
  setMeasurementDeniedInDocument(true);
  const saved = persist('denied');
  try { window.localStorage.removeItem(EXPLICIT_ANALYTICS_RECEIPT); } catch { /* the saved denial wins */ }
  clearBookingReceipts();
  if (consentModeReady) {
    pushConsentCommand('update', consentSignalsFor('denied'));
  }
  effectiveConsent = 'denied';
  listeners.forEach((listener) => listener({ type: 'decision', decision: 'denied' }));
  return saved;
}

/** Reopen the UI without erasing or changing the visitor's saved decision. */
export function reopenConsentChoices(): void {
  listeners.forEach((listener) => listener({ type: 'choices_requested' }));
}

export function onConsentEvent(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Compare the persisted choice itself after awaiting any preference write. */
export function getConsentReceipt(): string | null {
  try { return window.localStorage.getItem(CONSENT_STORAGE_KEY); } catch { return null; }
}
